import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  NgZone,
} from '@angular/core';
import { IonContent, ModalController } from '@ionic/angular';

export interface CalendarDayClasses {
  empty: boolean;
  disabled: boolean;
  selected: boolean;
  today: boolean;
  inRange: boolean;
  rangeStart: boolean;
  rangeEnd: boolean;
}

export interface CalendarDay {
  date: Date | null;
  day: number | null;
  /** Precomputed YYYY-MM-DD; '' for empty cells. Avoids per-CD-tick string allocation. */
  dateStr: string;
  price: number | null;
  disabled: boolean;
  isToday: boolean;
  badges?: CalendarBadge[];
  /** Mutated by refreshDayStates; never reassigned, so child bindings stay stable. */
  classes: CalendarDayClasses;
}

export interface CalendarBadge {
  label: string;
  color: string;
  textColor?: string;
}

export interface CalendarMonth {
  year: number;
  month: number; // 0-indexed
  label: string;
  weeks: CalendarDay[][];
}

export interface CalendarPriceEntry {
  date: string; // YYYY-MM-DD
  price: number;
}

export interface CalendarBadgeEntry {
  date: string; // YYYY-MM-DD
  badges: CalendarBadge[];
}

export type CalendarMode = 'single' | 'range';

export type CalendarRangeField = 'start' | 'end';

export interface CalendarRangeResult {
  startDate: string;
  endDate: string;
  hasEnd: boolean;
}

@Component({
  selector: 'app-calendar-modal',
  templateUrl: './calendar-modal.component.html',
  styleUrls: ['./calendar-modal.component.scss'],
})
export class CalendarModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(IonContent, { static: false }) content!: IonContent;

  @Input() mode: CalendarMode = 'single';
  @Input() currencySymbol: string = '₹';
  /** YYYY-MM-DD */
  @Input() minDate: string = '';
  /** YYYY-MM-DD */
  @Input() maxDate: string = '';
  @Input() monthsToShow: number = 6;
  @Input() prices: CalendarPriceEntry[] = [];
  @Input() badgeEntries: CalendarBadgeEntry[] = [];
  /** YYYY-MM-DD entries */
  @Input() disabledDates: string[] = [];

  /** YYYY-MM-DD (single mode) */
  @Input() selectedDate: string = '';
  @Input() title: string = 'Choose Date';
  @Input() confirmLabel: string = 'Confirm Date';

  /** YYYY-MM-DD (range mode) */
  @Input() startDate: string = '';
  /** YYYY-MM-DD (range mode). Empty implies a single-point selection. */
  @Input() endDate: string = '';
  @Input() initialField: CalendarRangeField = 'start';
  @Input() requireRangeEnd: boolean = false;

  /**
   * i18n keys for range-mode labels. Required when `mode='range'` — each caller
   * supplies its own domain wording (flight departure/return, hotel check-in/out,
   * car pickup/drop-off, …). No defaults so we can't accidentally render flight
   * copy in a non-flight context.
   */
  @Input() rangeStartKey: string = '';
  @Input() rangeEndKey: string = '';
  @Input() rangeStartLabelKey: string = '';
  @Input() rangeEndLabelKey: string = '';
  /** Only used when `requireRangeEnd=false` — labels the optional add-end CTA tile. */
  @Input() addRangeEndTitleKey: string = '';
  @Input() addRangeEndSubtitleKey: string = '';

  weekDays: string[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  months: CalendarMonth[] = [];

  selected: string = '';
  internalStart: string = '';
  internalEnd: string = '';
  activeField: CalendarRangeField = 'start';

  currentHeaderData: any = {
    title: this.title,
    subtitle: '',
    modalSlotEnd: true,
    marl20: true,
    mart4: '4px',
  };

  private priceMap: Map<string, number> = new Map();
  private badgeMap: Map<string, CalendarBadge[]> = new Map();
  private disabledSet: Set<string> = new Set();
  private minDateObj: Date | null = null;
  private maxDateObj: Date | null = null;

  private nextMonth: number = 0;
  private nextYear: number = 0;
  private isLoadingMore: boolean = false;
  private readonly BATCH_SIZE = 4;
  private today!: Date;
  private scrollEl?: HTMLElement;
  /** rAF gate so the scroll handler does at most one threshold check per frame. */
  private scrollFrameQueued = false;

  constructor(
    private modalController: ModalController,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    if (this.mode === 'range') {
      this.internalStart = this.startDate || '';
      this.internalEnd = this.endDate || '';
      this.activeField = this.initialField || 'start';
    } else {
      this.selected = this.selectedDate || '';
    }
    this.currentHeaderData = { ...this.currentHeaderData, title: this.title };
    this.buildLookups();
    this.buildMonths();
    this.refreshDayStates();
  }

  ngAfterViewInit(): void {
    this.scrollToSelectedMonth();
    this.attachScrollListener();
  }

  ngOnDestroy(): void {
    if (this.scrollEl) {
      this.scrollEl.removeEventListener('scroll', this.handleNativeScroll);
      this.scrollEl = undefined;
    }
  }

  closeModal(): void {
    this.modalController.dismiss(null);
  }

  confirm(): void {
    if (this.mode === 'range') {
      const result: CalendarRangeResult = {
        startDate: this.internalStart,
        endDate: this.internalEnd,
        hasEnd: !!this.internalEnd,
      };
      this.modalController.dismiss(result);
      return;
    }
    this.modalController.dismiss({ selectedDate: this.selected });
  }

  setActiveField(field: CalendarRangeField): void {
    if (this.mode !== 'range') return;
    this.activeField = field;
    this.refreshDayStates();
  }

  selectDay(day: CalendarDay): void {
    if (!day.date || day.disabled) return;
    const dateStr = day.dateStr;

    if (this.mode === 'single') {
      this.selected = dateStr;
      this.refreshDayStates();
      return;
    }

    if (this.activeField === 'start') {
      this.internalStart = dateStr;
      if (this.internalEnd && this.internalEnd <= this.internalStart) {
        this.internalEnd = '';
      }
      this.refreshDayStates();
      return;
    }

    // Toggle off when re-tapping the chosen end date. Start is never togglable
    // (a range always needs one), and requireRangeEnd disables this too (e.g.
    // hotel check-out is mandatory).
    if (
      !this.requireRangeEnd &&
      this.internalEnd &&
      dateStr === this.internalEnd
    ) {
      this.internalEnd = '';
      this.refreshDayStates();
      return;
    }

    if (this.internalStart && dateStr < this.internalStart) {
      // Defensive — should already be disabled, but treat as a new start.
      this.internalStart = dateStr;
      this.internalEnd = '';
      this.refreshDayStates();
      return;
    }
    this.internalEnd = dateStr;
    this.refreshDayStates();
  }

  onAddEndTap(): void {
    this.activeField = 'end';
    this.refreshDayStates();
    if (this.internalStart) {
      this.scrollToDate(this.internalStart, true);
    }
  }

  onStartSummaryTap(): void {
    this.activeField = 'start';
    this.refreshDayStates();
    if (this.internalStart) {
      this.scrollToDate(this.internalStart, true);
    }
  }

  onEndSummaryTap(): void {
    this.activeField = 'end';
    this.refreshDayStates();
    const target = this.internalEnd || this.internalStart;
    if (target) {
      this.scrollToDate(target, true);
    }
  }

  /**
   * O(n) sweep over every loaded day. Called on selection/activeField/append.
   * Cheaper than re-running the same predicates per cell on every CD tick.
   */
  private refreshDayStates(): void {
    const single = this.mode === 'single';
    const isRange = !single;
    const start = this.internalStart;
    const end = this.internalEnd;
    const sel = this.selected;
    const blockEnd = isRange && this.activeField === 'end' && !!start;

    for (const month of this.months) {
      for (const week of month.weeks) {
        for (const day of week) {
          const c = day.classes;
          if (!day.dateStr) continue;
          const ds = day.dateStr;
          let selected: boolean;
          let inRange = false;
          let rangeStart = false;
          let rangeEnd = false;
          if (single) {
            selected = ds === sel;
          } else {
            rangeStart = !!start && ds === start;
            rangeEnd = !!end && ds === end;
            selected = rangeStart || rangeEnd;
            inRange = !!start && !!end && ds > start && ds < end;
          }
          c.selected = selected;
          c.inRange = inRange;
          c.rangeStart = rangeStart;
          c.rangeEnd = rangeEnd;
          c.today = day.isToday && !selected;
          const blockedByRange = blockEnd
            ? this.requireRangeEnd
              ? ds <= start
              : ds < start
            : false;
          c.disabled = day.disabled || blockedByRange;
        }
      }
    }
  }

  trackMonth(_index: number, month: CalendarMonth): string {
    return `${month.year}-${month.month}`;
  }

  trackWeek(index: number): number {
    return index;
  }

  trackDay(index: number): number {
    return index;
  }

  get canConfirm(): boolean {
    if (this.mode === 'single') return !!this.selected;
    if (this.requireRangeEnd) {
      return !!this.internalStart && !!this.internalEnd;
    }
    return !!this.internalStart;
  }

  formatLong(dateStr: string): string {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return '-';
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }

  private buildLookups(): void {
    this.priceMap.clear();
    for (const entry of this.prices) {
      this.priceMap.set(entry.date, entry.price);
    }

    this.badgeMap.clear();
    for (const entry of this.badgeEntries) {
      this.badgeMap.set(entry.date, entry.badges);
    }

    this.disabledSet = new Set(this.disabledDates);

    this.minDateObj = this.minDate
      ? new Date(this.minDate + 'T00:00:00')
      : null;
    this.maxDateObj = this.maxDate
      ? new Date(this.maxDate + 'T00:00:00')
      : null;
  }

  private buildMonths(): void {
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);

    const startDate = this.minDateObj
      ? new Date(this.minDateObj)
      : new Date(this.today);
    this.nextMonth = startDate.getMonth();
    this.nextYear = startDate.getFullYear();

    this.months = [];
    this.appendMonths(this.monthsToShow);
  }

  private appendMonths(count: number): void {
    for (let i = 0; i < count; i++) {
      if (this.maxDateObj) {
        const firstOfMonth = new Date(this.nextYear, this.nextMonth, 1);
        if (firstOfMonth > this.maxDateObj) return;
      }

      const month = this.buildMonth(this.nextYear, this.nextMonth, this.today);
      this.months.push(month);

      this.nextMonth++;
      if (this.nextMonth > 11) {
        this.nextMonth = 0;
        this.nextYear++;
      }
    }
  }

  /**
   * Scroll listener runs outside the Angular zone so high-frequency scroll
   * events don't trigger CD. We only re-enter the zone to append new months.
   */
  private attachScrollListener(): void {
    this.ngZone.runOutsideAngular(async () => {
      try {
        const el = await this.content?.getScrollElement();
        if (!el) return;
        this.scrollEl = el;
        el.addEventListener('scroll', this.handleNativeScroll, {
          passive: true,
        });
      } catch {
        // Best-effort — if Ionic hasn't wired up the scroll element yet, lazy load is just disabled.
      }
    });
  }

  private handleNativeScroll = (): void => {
    if (this.scrollFrameQueued || this.isLoadingMore) return;
    this.scrollFrameQueued = true;
    requestAnimationFrame(() => {
      this.scrollFrameQueued = false;
      const el = this.scrollEl;
      if (!el) return;
      const scrollPercent =
        (el.scrollTop + el.clientHeight) / (el.scrollHeight || 1);
      if (scrollPercent < 0.8 || this.isLoadingMore) return;
      this.isLoadingMore = true;
      this.ngZone.run(() => {
        this.appendMonths(this.BATCH_SIZE);
        this.refreshDayStates();
        this.isLoadingMore = false;
      });
    });
  };

  private buildMonth(year: number, month: number, today: Date): CalendarMonth {
    const label = new Date(year, month, 1)
      .toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
      })
      .toUpperCase();

    const firstDay = new Date(year, month, 1);
    // Monday = 0, Sunday = 6 (ISO-like)
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const weeks: CalendarDay[][] = [];
    let currentWeek: CalendarDay[] = [];

    for (let i = 0; i < startDow; i++) {
      currentWeek.push(this.emptyDay());
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      const dateStr = this.formatDate(date);

      const isToday =
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate();

      let disabled = false;
      if (this.disabledSet.has(dateStr)) disabled = true;
      if (this.minDateObj && date < this.minDateObj) disabled = true;
      if (this.maxDateObj && date > this.maxDateObj) disabled = true;
      if (date < today) disabled = true;

      const price = this.priceMap.get(dateStr) ?? null;
      const badges = this.badgeMap.get(dateStr) ?? undefined;

      currentWeek.push({
        date,
        day: d,
        dateStr,
        price,
        disabled,
        isToday,
        badges,
        classes: {
          empty: false,
          disabled,
          selected: false,
          today: isToday,
          inRange: false,
          rangeStart: false,
          rangeEnd: false,
        },
      });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(this.emptyDay());
      }
      weeks.push(currentWeek);
    }

    return { year, month, label, weeks };
  }

  private emptyDay(): CalendarDay {
    return {
      date: null,
      day: null,
      dateStr: '',
      price: null,
      disabled: true,
      isToday: false,
      classes: {
        empty: true,
        disabled: true,
        selected: false,
        today: false,
        inRange: false,
        rangeStart: false,
        rangeEnd: false,
      },
    };
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private getInitialFocusDate(): string {
    if (this.mode === 'single') return this.selected;
    if (this.activeField === 'end' && this.internalEnd) {
      return this.internalEnd;
    }
    return this.internalStart || this.internalEnd;
  }

  /**
   * Computes the exact gap from the append cursor to the target and appends in
   * one call rather than batching. Matters for large ranges (e.g. end date 12+
   * months out) where batched appending was slow and could hit the attempt cap.
   */
  private ensureMonthLoaded(year: number, month: number): void {
    const isLoaded = this.months.some(
      (mo) => mo.year === year && mo.month === month
    );
    if (isLoaded) return;

    const monthsNeeded =
      (year - this.nextYear) * 12 + (month - this.nextMonth) + 1;
    if (monthsNeeded > 0) {
      this.appendMonths(monthsNeeded);
    }
  }

  private async scrollToSelectedMonth(): Promise<void> {
    const dateStr = this.getInitialFocusDate();
    await this.scrollToDate(dateStr, false);
  }

  /**
   * When many months are appended in one tick, Angular needs a few frames to
   * render the new month nodes. Poll across up to ~10 frames for the target
   * element to appear instead of measuring blindly on the first rAF.
   */
  private async scrollToDate(
    dateStr: string,
    animate: boolean
  ): Promise<void> {
    if (!dateStr) return;

    const [y, m] = dateStr.split('-').map(Number);
    if (!y || !m) return;
    const targetYear = y;
    const targetMonth = m - 1;

    this.ensureMonthLoaded(targetYear, targetMonth);
    this.refreshDayStates();

    const scrollEl = await this.content?.getScrollElement();
    if (!scrollEl) return;
    const targetId = `month-${targetYear}-${targetMonth}`;

    const performScroll = (el: HTMLElement) => {
      // Weekday row is position:sticky — subtract its height so the month label
      // and first week aren't hidden behind it.
      const stickyHeader = scrollEl.querySelector<HTMLElement>('.weekday-row');
      const stickyOffset = stickyHeader?.offsetHeight ?? 0;

      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      const viewTop = scrollEl.scrollTop + stickyOffset;
      const viewBottom = scrollEl.scrollTop + scrollEl.clientHeight;

      const alreadyVisible = elTop >= viewTop && elBottom <= viewBottom;
      if (alreadyVisible) return;

      const targetTop = Math.max(0, elTop - stickyOffset - 8);
      if (animate) {
        scrollEl.scrollTo({ top: targetTop, behavior: 'smooth' });
      } else {
        scrollEl.scrollTop = targetTop;
      }
    };

    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    const tryScroll = () => {
      const el = document.getElementById(targetId);
      if (el) {
        performScroll(el);
        return;
      }
      if (attempts++ < MAX_ATTEMPTS) {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  }
}
